// Normalising Firestore time values before anything tries to read them.
//
// A category's startTime/cutOffTime has been stored three different ways over
// the life of this project: an ISO string, a Date, and — since the schedule was
// rewritten — a Firestore Timestamp. Neither `new Date(timestamp)` nor
// `moment(timestamp)` can parse a Timestamp object: the first yields Invalid
// Date, the second an invalid moment. Both fail silently, and the caller ends
// up rendering a fallback that looks legitimate ("WAITING FOR START", or a
// datetime input quietly showing 00:00) rather than an obvious error.
//
// Every read of a stored time should go through here.

/**
 * Any stored time value → a real Date, or null when it is absent/unparseable.
 * Accepts a Firestore Timestamp (client or admin shape), a Date, an ISO string,
 * or epoch millis.
 */
export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  // Plain-object Timestamp: survives JSON transport, where toDate() does not.
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value._seconds === "number") return new Date(value._seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Any stored time value → epoch millis, or NaN when it cannot be read. */
export const toMillis = (value) => {
  const date = toDate(value);
  return date ? date.getTime() : NaN;
};
